import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react"
import { IconButton, Tooltip } from "@material-ui/core"
import { MdOutlineCancel } from "react-icons/md"
import { ModalTakePicture } from "./ModalTakePicture"
import { ModalEditPicture } from "./ModalEditPicture"
import { FiDownload } from "react-icons/fi"
import { useGlobal } from "../../../hooks/global"
import { useApi } from "../../../hooks/api"
import { fileProps } from "../../../interfaces"
import { IInputDocumentRefProps, InputDocument } from "../InputFile"
import { borderColors } from "../../../theme"
import { Line } from "../../../style"
import { UploadIcon } from "../../SvgComponents/Icons/UploadIcon"
import { MyCircularProgress } from "../../MyCircularProgress"

interface IInputImagee {
    defaultDocument: string | null
    onChange: (file: string | null) => void
    type: 'document' | 'selfie'
    fileOnly: boolean,
    getIsDisabled?: (form: any) => boolean;
}

const InputImageComponent: React.ForwardRefRenderFunction<
    IInputDocumentRefProps,
    IInputImagee
> = (props, ref) => {
    const { defaultDocument, onChange, type, fileOnly, getIsDisabled, ...rest } = props;

    const [doc, setDoc] = useState<string | null>(defaultDocument)
    const { theme } = useGlobal()
    const { api } = useApi()
    const [loading, setLoading] = useState(false);
    const [file, setFile] = useState<fileProps | null>(null);

    const [documentHover, setDocumentHover] = useState<boolean>(false)

    const anchor_ref = useRef<HTMLAnchorElement>(null)
    const inputFileRef = useRef<IInputDocumentRefProps>(null);
    const [openModal, setOpenModal] = useState<'document' | 'selfie' | null>(null)
    const [editModal, setEditModal] = useState<boolean>(false)
    const [mirrored, setMirrored] = useState<boolean>(false)

    const transformImageInBase64 = useCallback(async (image: any) => {
        const maxSize = 500 * 1024; // 500KB

        if (image?.file?.size > maxSize) {
            alert("O arquivo é muito grande! Escolha um arquivo menor que 500KB.");
            return;
        }

        setLoading(true);
        try {
            const formData = new FormData();
            formData.append("photo", image.file);

            const { data } = await api.post("/photos/transform", formData);

            setDoc(data.complete);
        } catch (err) {
            console.error(err);;
        }
        setLoading(false);
    }, []);

    const handleDocumentDestroy = (document: any) => {
        const confirmed = window.confirm("Deseja realmente excluir este documento?")
        if (confirmed) setDoc(null)
    }

    const handleChangeDocument = (_isloading: boolean, type: 'document' | 'selfie') => {
        if (_isloading) return;

        if (fileOnly) {
            inputFileRef.current?.focus();
            return;
        }

        const confirmedSelfie = window.confirm("Deseja utilizar a câmera para tirar a foto?\n\nClique em 'OK' para tirar a foto.\n\nClique em 'Cancelar' para escolher um arquivo.");

        if (!confirmedSelfie) {
            inputFileRef.current?.focus();
        } else {
            setOpenModal(type);
        }
    }

    useEffect(() => {
        if (file) transformImageInBase64(file);
    }, [file]);

    useEffect(() => {
        onChange(doc)
    }, [doc]);

    const isDisabled = getIsDisabled ? getIsDisabled('form') : false;

    useImperativeHandle(ref, () => ({ focus }));
    return (
        <div style={{ display: "flex", flexDirection: "column", flex: 1, gap: 10 }}>
            {!!doc && (
                <div onMouseMove={() => setDocumentHover(true)} onMouseLeave={() => setDocumentHover(false)}
                    style={{ position: "relative", flex: 1, cursor: `${!isDisabled ? 'pointer': 'cursor'}`, border: `1px solid ${borderColors[theme]}`, borderRadius: 5 }}>
                    {documentHover && !isDisabled &&
                        <Line style={{ gap: 10, position: "absolute", top: -10, right: -10, zIndex: 99 }}>
                            <Tooltip onClick={() => anchor_ref?.current?.click()} style={{ margin: 0, padding: 10, borderRadius: 5, background: "#7041ff" }} title="Baixar">
                                <IconButton aria-label="Baixar">
                                    <FiDownload
                                        color={"#FFF"}
                                        size={20}
                                    />
                                </IconButton>
                            </Tooltip>
                            <Tooltip onClick={handleDocumentDestroy} style={{ margin: 0, padding: 10, borderRadius: 5, background: "#FF6B6B" }} title="Excluir">
                                <IconButton aria-label="Excluir">
                                    <MdOutlineCancel
                                        color={"#FFF"}
                                        size={20}
                                    />
                                </IconButton>
                            </Tooltip>
                        </Line>
                    }
                    <div onClick={() => !isDisabled ? setEditModal(true) : {}}
                    >
                        <img src={doc} style={{
                            width: '100%',
                            maxHeight: 230,
                            height: 'auto',
                            objectFit: 'contain',
                            transform: mirrored ? 'scale(-1,1)' : undefined,
                            borderRadius: 5,
                            padding: 10
                        }} />
                    </div>
                    <a ref={anchor_ref} style={{ display: "none" }} target="_blank" href={`${doc}`} download={`${file?.file?.name.toUpperCase()}`}>Download</a>
                </div >
            )}
            {!doc && (
                <div style={{ display: "flex", flex: 1, minHeight: 200, maxHeight: 200, maxWidth: 350 }}>
                    <div style={{ display: "none" }}>
                        <InputDocument
                            ref={inputFileRef}
                            onChange={(e) => setFile(e)}
                            fileTypes={["jpeg", "jpg", "png"]}
                        />
                    </div>
                    <div
                        onClick={() => handleChangeDocument(loading, type)}
                        style={{
                            display: "flex",
                            padding: 0,
                            justifyContent: "center",
                            alignItems: "center",
                            border: "2px dashed #E5E5E5",
                            borderRadius: 5,
                            flexDirection: "column",
                            flex: 1,
                            cursor: !loading ? "pointer" : "default",
                            marginBottom: 20,
                        }}
                    >
                        {!loading && (
                            <>
                                <UploadIcon />
                                <div
                                    style={{
                                        fontWeight: "bold",
                                        fontSize: 15,
                                        textAlign: "center",
                                        maxWidth: 280,
                                        marginTop: 20,
                                    }}
                                >
                                    Clique aqui ou arraste um arquivo para carregar
                                </div>
                                <div style={{ fontSize: 14 }}>Formato PNG, JPG ou JPEG.</div>
                            </>
                        )}
                        {loading && (
                            <>
                                <MyCircularProgress color="#7041ff" size={30} />
                                <div style={{ marginTop: 20, fontWeight: "bold" }}>
                                    Carregando documento
                                </div>
                                <div style={{ fontSize: 13 }}>Processando a imagem...</div>
                            </>
                        )}
                    </div>
                </div>
            )}
            {openModal && (
                <ModalTakePicture
                    onClose={() => setOpenModal(null)}
                    onTakePicture={(image) => {
                        setDoc(image);
                        setOpenModal(null);
                    }}
                    type={openModal}
                />
            )}
            {editModal && defaultDocument && (
                <ModalEditPicture
                    onClose={() => setEditModal(false)}
                    onTakePicture={(image) => {
                        setDoc(image);
                        setEditModal(false);
                    }}
                    type={type}
                    defaultImage={defaultDocument}
                />
            )}
        </div>
    )
}

export const InputImage = forwardRef(InputImageComponent);